import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatJobNumber, intervalLabel, type RepeatSlot } from '@fieldops/shared'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { addressLine, money } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { AgreementBuilder } from '../agreements/AgreementBuilder.js'
import { BILLING_LABEL } from '../agreements/builder-model.js'
import { trackSub } from '../agreements/AgreementGrid.js'
import { Icon } from '../Icon.js'
import type { RepeatForJob, ScheduleView } from '../repeat/drafts.js'
import { anchorLabel, combineLabel, dayLabel, endsLabel, leadLabel, servicesTotal } from '../repeat/rule.js'
import { ServiceTracks } from '../repeat/ServiceTracks.js'
import { Button, ErrorState, Input, Textarea } from '../ui.js'
import { Label, Panel, PanelHeading } from './panes.js'
import type { Job } from './types.js'

type SiteAgreement = NonNullable<RepeatForJob['agreement']>

/** How many visits before this job the timeline draws before folding the rest. */
const EARLIER_SHOWN = 3

function slotKey(slot: { scheduleId: string; sequence: number }): string {
  return `${slot.scheduleId}:${slot.sequence}`
}

/** "A", "A and B", "A, B and C". */
function andList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? 'Service'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The job card's Schedule tab. Decision 96.
 *
 * A schedule is the rule; its visits are computed. The timeline is the last
 * few visits, this job, a red Today, the next visit and two planned after it.
 * The next visit is a card of its own because that is the one the office acts
 * on: its date and note can be changed for that visit alone, and its job can
 * be created early or it can be skipped. The rule itself is the rail.
 *
 * A schedule that is part of a service agreement draws every service the
 * agreement has at this site instead, month by month across the term, and the
 * card is the soonest of their visits. The rail is then the agreement.
 *
 * A job from a ServiceM8 series with no schedule reads the series, read-only,
 * with Start schedule prefilled from its interval.
 */
export function SchedulePane({
  job,
  data,
  canSchedule,
  onStart,
  onAgreement,
}: {
  job: Job
  data: RepeatForJob
  canSchedule: boolean
  onStart: (legacyRecurrenceId: string | null) => void
  /** Opens the agreement builder from this job; absent without `CLIENT_EDIT`. */
  onAgreement?: () => void
}) {
  const schedule = data.schedule

  if (!schedule) {
    if (data.imported && data.imported.jobs.length > 0) {
      return <ImportedChain data={data} canSchedule={canSchedule} onStart={onStart} />
    }
    return (
      <Panel className="items-center gap-3 py-10 text-center">
        <span className="grid size-11 place-items-center rounded-full bg-ink-100 text-ink-500">
          <Icon name="repeat" />
        </span>
        <p className="text-[15px] font-semibold text-ink-900">This job doesn’t repeat</p>
        <p className="max-w-sm text-[13px] text-ink-500">
          Make it repeat and the next visit’s job is made for you, with the same site, services,
          price and people.
        </p>
        {canSchedule ? (
          job.site ? (
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="primary" icon={<Icon name="repeat" className="size-4" />} onClick={() => onStart(null)}>
                Make this job repeat
              </Button>
              {onAgreement ? (
                <Button icon={<Icon name="agreement" className="size-4" />} onClick={onAgreement}>
                  Set up a service agreement
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="text-[12.5px] text-amber-800">Give the job its site first.</p>
          )
        ) : null}
      </Panel>
    )
  }

  if (data.agreement) {
    return <AgreementSchedule job={job} data={data} agreement={data.agreement} canSchedule={canSchedule} />
  }

  const next = schedule.slots.find((slot) => slot.isNext) ?? null
  const visits = schedule.slots.filter((slot) => slot.job).length + (data.imported?.recurrenceId === schedule.legacyRecurrenceId ? data.imported.jobs.length : 0)

  return (
    <>
      <Panel className="gap-4">
        <PanelHeading
          title="Visits"
          hint={`${visits} ${visits === 1 ? 'visit' : 'visits'} · repeats ${intervalLabel(schedule).replace(/^E/, 'e')}`}
        />
        <ScheduleTimeline job={job} data={data} schedule={schedule} canSchedule={canSchedule} />
      </Panel>

      <Refusal schedule={schedule} />

      {next && !schedule.refusal ? (
        <NextVisitCard
          key={`${schedule.id}-${next.sequence}`}
          title="Next visit"
          schedule={schedule}
          slot={next}
          data={data}
          canSchedule={canSchedule}
        />
      ) : null}
    </>
  )
}

function Refusal({ schedule }: { schedule: ScheduleView }) {
  if (!schedule.refusal) return null
  return (
    <p role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900 ring-1 ring-amber-200 ring-inset">
      {refusalSentence(schedule.refusal)}
    </p>
  )
}

function refusalSentence(refusal: string): string {
  switch (refusal) {
    case 'STOPPED':
      return 'This schedule is stopped. Nothing further is planned; jobs already made are unchanged.'
    case 'AGREEMENT_ENDED':
      return 'The service agreement this schedule is part of has ended.'
    case 'AGREEMENT_PAUSED':
      return 'The service agreement is paused, so no new jobs are made until it is resumed.'
    default:
      return 'This schedule has been removed.'
  }
}

function siteName(site: { name: string | null; address: Parameters<typeof addressLine>[0] }): string {
  return site.name ?? addressLine(site.address)
}

/**
 * Every service the agreement has at this job's site, across the term, and
 * the soonest visit among them to act on.
 */
function AgreementSchedule({
  job,
  data,
  agreement,
  canSchedule,
}: {
  job: Job
  data: RepeatForJob
  agreement: SiteAgreement
  canSchedule: boolean
}) {
  const live = agreement.schedules.filter((row) => !row.refusal)
  const upcoming =
    live
      .flatMap((row) => row.slots.filter((slot) => slot.isNext).map((slot) => ({ row, slot })))
      .sort((a, b) => a.slot.dueOn.localeCompare(b.slot.dueOn))[0] ?? null
  const own = data.schedule!

  return (
    <>
      <Panel className="gap-4">
        <PanelHeading
          title="Services at this site"
          hint={`Term ${dayLabel(agreement.startsOn)} – ${dayLabel(agreement.termEndsOn)}`}
        />
        <ServiceTracks
          label="Services at this site"
          months={agreement.months}
          today={data.today}
          currentJobId={job.id}
          upcoming={upcoming ? { rowId: upcoming.row.id, sequence: upcoming.slot.sequence } : null}
          groups={[
            {
              key: agreement.siteId,
              rows: agreement.schedules.map((row) => ({
                id: row.id,
                name: row.services.map((service) => service.name).join(', ') || 'No service',
                sub: trackSub({ ...row, priceIncTax: servicesTotal(row.services) || null }),
                slots: row.slots,
              })),
            },
          ]}
        />
      </Panel>

      <Refusal schedule={own} />

      {upcoming ? (
        <NextVisitCard
          key={`${upcoming.row.id}-${upcoming.slot.sequence}`}
          title="Upcoming visit"
          service={upcoming.row.services.map((service) => service.name).join(', ')}
          schedule={upcoming.row}
          slot={upcoming.slot}
          data={data}
          canSchedule={canSchedule}
        />
      ) : null}
    </>
  )
}

type Node = {
  key: string
  day: string
  word: string | null
  tone: 'done' | 'open' | 'lost' | 'skipped' | 'overdue' | 'next' | 'planned'
  current: boolean
  jobId: string | null
  jobNumber: string | null
  tentative: boolean
  detail: string | null
  slot: RepeatSlot | null
}

const TONE: Record<RepeatSlot['state'], Node['tone']> = {
  DONE: 'done',
  CREATED: 'open',
  UNSUCCESSFUL: 'lost',
  SKIPPED: 'skipped',
  OVERDUE: 'overdue',
  NEXT: 'next',
  PLANNED: 'planned',
}

/** What is written under a visit's date when it has no job number. */
const DETAIL: Partial<Record<RepeatSlot['state'], string>> = {
  NEXT: 'Not created yet',
  OVERDUE: 'Overdue',
  PLANNED: 'Planned',
  SKIPPED: 'Skipped',
}

/** Behind the series: the green, solid part of the line. */
const settled = (tone: Node['tone']) => tone === 'done' || tone === 'lost' || tone === 'skipped'

/**
 * The visits, left to right, each an equal column: a dot on one line, its
 * date and its job number. The line is solid green through what is done and
 * dashed grey into what is planned; the next visit stands in a green card,
 * and a red Today falls between the columns it separates. Earlier visits fold
 * into a chip past the last three; ServiceM8 jobs a schedule took over lead.
 */
function ScheduleTimeline({
  job,
  data,
  schedule,
  canSchedule,
}: {
  job: Job
  data: RepeatForJob
  schedule: ScheduleView
  canSchedule: boolean
}) {
  const [showAll, setShowAll] = useState(false)
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const unskip = useMutation(
    trpc.repeat.unskipVisit.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.repeat.pathFilter()),
    }),
  )

  const history: Node[] =
    data.imported && data.imported.recurrenceId === schedule.legacyRecurrenceId
      ? data.imported.jobs.map((row) => ({
          key: `history-${row.id}`,
          day: row.on,
          word: row.id === job.id ? 'This job' : null,
          tone: 'done',
          current: row.id === job.id,
          jobId: row.id,
          jobNumber: formatJobNumber(row),
          tentative: false,
          detail: 'ServiceM8',
          slot: null,
        }))
      : []

  const slots: Node[] = schedule.slots.map((slot) => {
    const numbered = slot.job ? schedule.jobs[slot.job.id] : null
    const current = slot.job?.id === job.id
    return {
      key: `slot-${slot.sequence}`,
      day: slot.dueOn,
      word: current ? 'This job' : slot.state === 'NEXT' || slot.state === 'OVERDUE' ? 'Next' : null,
      tone: TONE[slot.state],
      current,
      jobId: slot.job?.id ?? null,
      jobNumber: numbered ? formatJobNumber(numbered) : null,
      tentative: slot.tentative,
      detail: slot.skipReason ?? (slot.moved ? 'Moved' : (DETAIL[slot.state] ?? null)),
      slot,
    }
  })

  const all = [...history, ...slots]
  const pivot = Math.max(
    0,
    all.findIndex((node) => node.current || node.tone === 'next' || node.tone === 'planned'),
  )
  const hidden = showAll ? 0 : Math.max(0, pivot - EARLIER_SHOWN)
  const nodes = all.slice(hidden)
  const lead = hidden > 0 ? 1 : 0
  const columns = nodes.length + lead
  const firstAfter = nodes.findIndex((node) => node.day > data.today)
  const todayAt = lead + (firstAfter === -1 ? nodes.length : firstAfter)

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto pb-1">
        <ol
          aria-label="Visits"
          className="relative grid pt-3"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(100px, 1fr))` }}
        >
          {hidden > 0 ? (
            <li className="flex flex-col items-center pt-7">
              <span className="relative flex h-7 w-full items-center justify-center">
                <span aria-hidden="true" className="absolute top-1/2 right-0 left-1/2 h-0.5 -translate-y-1/2 bg-green-500" />
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="relative rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold text-ink-600 ring-1 ring-ink-200 hover:bg-ink-50"
                >
                  {hidden} earlier
                </button>
              </span>
            </li>
          ) : null}
          {nodes.map((node, index) => {
            const before = index > 0 ? nodes[index - 1]! : hidden > 0 ? all[hidden - 1]! : null
            const after = nodes[index + 1] ?? null
            return (
              <TimelineStep
                key={node.key}
                node={node}
                lineIn={before ? (settled(before.tone) && settled(node.tone) ? 'solid' : 'dashed') : null}
                lineOut={after ? (settled(node.tone) && settled(after.tone) ? 'solid' : 'dashed') : null}
                onUnskip={
                  canSchedule && node.slot?.state === 'SKIPPED' && !node.slot.job
                    ? () => unskip.mutate({ scheduleId: schedule.id, sequence: node.slot!.sequence })
                    : undefined
                }
              />
            )
          })}
          <TodayMarker at={todayAt} of={columns} />
        </ol>
      </div>
      {unskip.error ? <ErrorState message={errorMessage(unskip.error)} /> : null}
    </div>
  )
}

/** A red pill and a dashed red rule, on the boundary between two columns. */
function TodayMarker({ at, of }: { at: number; of: number }) {
  const edge = at === 0 ? 'translate-x-0' : at === of ? '-translate-x-full' : '-translate-x-1/2'
  return (
    <li
      role="separator"
      aria-label="Today"
      className={cn('pointer-events-none absolute top-0 bottom-0 z-10 flex flex-col items-center', edge)}
      style={{ left: `${(at / of) * 100}%` }}
    >
      <span className="rounded bg-red-600 px-1.5 py-px text-[9.5px] font-bold tracking-wide text-white uppercase shadow-sm">
        Today
      </span>
      <span className="w-0 flex-1 border-l-2 border-dashed border-red-400" />
    </li>
  )
}

/**
 * Half of the line between two visits. It runs through the column's padding
 * to the column's edge, and a dash is anchored at that edge — whole tiles
 * end one half and start the next — so the two halves meet as one line.
 */
function Line({ side, kind }: { side: 'in' | 'out'; kind: 'solid' | 'dashed' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute top-1/2 h-0.5 -translate-y-1/2',
        side === 'in' ? 'right-1/2 -left-1' : '-right-1 left-1/2',
        kind === 'solid'
          ? 'bg-green-500'
          : cn(
              'bg-[linear-gradient(to_right,var(--color-ink-300)_6px,transparent_6px)] bg-[length:10px_2px] bg-repeat-x',
              side === 'in' ? 'bg-left' : 'bg-right',
            ),
      )}
    />
  )
}

function Dot({ tone }: { tone: Node['tone'] }) {
  switch (tone) {
    case 'done':
      return (
        <span className="grid size-7 place-items-center rounded-full bg-green-600 text-white shadow-[0_0_0_4px_white]">
          <Icon name="check" className="size-4" strokeWidth={3} />
        </span>
      )
    case 'open':
      return (
        <span className="grid size-7 place-items-center rounded-full bg-blue-50 shadow-[0_0_0_4px_white] ring-2 ring-blue-500 ring-inset">
          <span className="size-2.5 rounded-full bg-blue-500" />
        </span>
      )
    case 'lost':
      return (
        <span className="grid size-7 place-items-center rounded-full bg-red-50 text-red-600 shadow-[0_0_0_4px_white] ring-2 ring-red-400 ring-inset">
          <Icon name="close" className="size-3.5" strokeWidth={3} />
        </span>
      )
    case 'skipped':
      return <span className="block size-5 rounded-full bg-ink-200 shadow-[0_0_0_4px_white]" />
    case 'overdue':
      return (
        <span className="grid size-7 place-items-center rounded-full bg-amber-50 shadow-[0_0_0_4px_white] ring-2 ring-amber-500 ring-inset">
          <span className="size-2.5 rounded-full bg-amber-500" />
        </span>
      )
    case 'next':
      return (
        <span className="grid size-7 place-items-center rounded-full bg-white text-green-700 shadow-[0_0_0_4px_white] ring-2 ring-green-600 ring-inset">
          <Icon name="repeat" className="size-3.5" strokeWidth={2.5} />
        </span>
      )
    default:
      return <span className="block size-5 rounded-full border-2 border-ink-300 bg-white shadow-[0_0_0_4px_white]" />
  }
}

function TimelineStep({
  node,
  lineIn,
  lineOut,
  onUnskip,
}: {
  node: Node
  lineIn: 'solid' | 'dashed' | null
  lineOut: 'solid' | 'dashed' | null
  onUnskip?: () => void
}) {
  const hrefFor = useJobOverlay().hrefFor
  const upcoming = node.tone === 'next' || node.tone === 'overdue'

  return (
    <li
      aria-current={node.current ? 'step' : undefined}
      className="relative flex flex-col items-center px-1 pb-1 text-center"
    >
      {upcoming ? (
        <span
          aria-hidden="true"
          className={cn(
            'absolute inset-x-1 -top-1 bottom-0 rounded-xl bg-white shadow-sm ring-2 ring-inset',
            node.tone === 'overdue' ? 'ring-amber-400' : 'ring-green-500',
          )}
        />
      ) : null}
      <span
        className={cn(
          'relative flex h-7 items-end pb-1.5 text-[10px] font-bold tracking-[0.06em] uppercase',
          node.tone === 'next' ? 'text-green-700' : node.tone === 'overdue' ? 'text-amber-700' : 'text-ink-700',
        )}
      >
        {node.word}
      </span>
      <span className="relative flex h-7 w-full items-center justify-center">
        {lineIn ? <Line side="in" kind={lineIn} /> : null}
        {lineOut ? <Line side="out" kind={lineOut} /> : null}
        <span className="relative grid place-items-center">
          <Dot tone={node.tone} />
        </span>
      </span>
      <span
        className={cn(
          'relative mt-2 text-[13px] tabular-nums',
          node.tone === 'planned' ? 'font-medium text-ink-500' : 'font-semibold text-ink-900',
          node.tone === 'skipped' && 'text-ink-400 line-through',
        )}
      >
        {node.tentative ? '≈ ' : ''}
        {dayLabel(node.day)}
      </span>
      <span className="relative flex min-h-4 flex-col items-center gap-0.5 pb-1.5">
        {node.jobId && node.jobNumber ? (
          node.current ? (
            <span className="font-mono text-[11.5px] text-ink-500">{node.jobNumber}</span>
          ) : (
            <Link to={hrefFor(node.jobId)} className="font-mono text-[11.5px] text-brand-700 hover:underline">
              {node.jobNumber}
            </Link>
          )
        ) : null}
        {node.detail ? (
          <span
            className={cn('max-w-full truncate text-[11.5px]', upcoming ? 'text-ink-600' : 'text-ink-400')}
            title={node.detail}
          >
            {node.detail}
          </span>
        ) : null}
        {onUnskip ? (
          <button type="button" onClick={onUnskip} className="text-[11.5px] font-semibold text-ink-500 hover:text-ink-900">
            Bring back
          </button>
        ) : null}
      </span>
    </li>
  )
}

/**
 * The visit to act on: its day, when its job will be made, a note for it,
 * and the three things the office does with it.
 */
function NextVisitCard({
  title,
  service,
  schedule,
  slot,
  data,
  canSchedule,
}: {
  title: string
  service?: string
  schedule: ScheduleView
  slot: RepeatSlot
  data: RepeatForJob
  canSchedule: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const overlay = useJobOverlay()

  const [note, setNote] = useState(slot.note ?? '')
  const [moving, setMoving] = useState(false)
  const [dueOn, setDueOn] = useState(slot.dueOn)
  const [skipping, setSkipping] = useState(false)
  const [reason, setReason] = useState('')
  const [opening, setOpening] = useState(false)
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(() => new Set())

  const target = { scheduleId: schedule.id, sequence: slot.sequence }
  const companions = useQuery({
    ...trpc.repeat.companions.queryOptions(target),
    enabled: canSchedule,
  })
  const others = companions.data?.companions ?? []
  const chosen = others
    .filter((other) => !unticked.has(slotKey(other)))
    .map(({ scheduleId, sequence }) => ({ scheduleId, sequence }))
  const createInput = chosen.length > 0 ? { ...target, with: chosen } : target
  const toggle = (key: string, on: boolean) =>
    setUnticked((current) => {
      const next = new Set(current)
      if (on) next.delete(key)
      else next.add(key)
      return next
    })

  const refresh = () => {
    void queryClient.invalidateQueries(trpc.repeat.pathFilter())
    void queryClient.invalidateQueries(trpc.agreements.pathFilter())
  }
  const saveNote = useMutation(trpc.repeat.setVisitNote.mutationOptions({ onSuccess: refresh }))
  const move = useMutation(trpc.repeat.moveVisit.mutationOptions({ onSuccess: refresh }))
  const skip = useMutation(trpc.repeat.skipVisit.mutationOptions({ onSuccess: refresh }))
  const create = useMutation(
    trpc.repeat.createJob.mutationOptions({
      onSuccess: () => {
        refresh()
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
      },
    }),
  )
  const error = saveNote.error ?? move.error ?? skip.error ?? create.error

  const lead = leadLabel(data.settings.leadDays)
  const booked =
    data.settings.autoBook && schedule.bookWith !== 'UNASSIGNED' && schedule.bookWithName
      ? `, and booked with ${schedule.bookWithName}`
      : ''
  const alongside =
    others.length > 0
      ? ` ${andList(others.flatMap((other) => other.serviceNames))} ${others.length === 1 ? 'goes' : 'go'} on the same job.`
      : ''
  const whenMade = data.settings.autoCreate
    ? `Not created yet. It will go onto the schedule on ${dayLabel(slot.createOn)}, ${lead} before it’s due${booked}.${alongside}`
    : `Not created yet. It is due to be made on ${dayLabel(slot.createOn)}, ${lead} before it’s due. Automatic creation is off in Settings, so make it here.`

  return (
    <Panel className="gap-4">
      <div className="flex items-center gap-2.5">
        <Label>{title}</Label>
        {service ? <span className="text-[12.5px] font-semibold text-ink-800">{service}</span> : null}
        <span className="ml-auto text-[11.5px] text-ink-400">{`Visit ${slot.sequence}`}</span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="text-[22px] leading-tight font-bold text-ink-900 tabular-nums">
            {slot.tentative ? '≈ ' : ''}
            {dayLabel(slot.dueOn)}
            {slot.state === 'OVERDUE' ? (
              <span className="ml-2 align-middle text-[12px] font-semibold text-amber-700">Overdue</span>
            ) : null}
          </p>
          {canSchedule && !moving ? (
            <button
              type="button"
              onClick={() => setMoving(true)}
              className="text-[12.5px] font-semibold text-brand-700 hover:underline"
            >
              Change the date
            </button>
          ) : null}
        </div>
        <p className="text-[13px] text-ink-600">
          {slot.tentative
            ? 'Counted from when the last job is completed, so this moves once it is.'
            : whenMade}
        </p>
      </div>

      {canSchedule && moving ? (
        <div className="flex flex-wrap items-end gap-2 rounded-lg bg-ink-50 p-3">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-700">
            Due date for this visit only
            <Input type="date" value={dueOn} onChange={(event) => setDueOn(event.target.value)} className="w-44" />
          </label>
          <Button size="sm" onClick={() => { setMoving(false); setDueOn(slot.dueOn) }}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!dueOn || dueOn === slot.dueOn}
            loading={move.isPending}
            onClick={() => move.mutate({ ...target, dueOn }, { onSuccess: () => setMoving(false) })}
          >
            Move this visit
          </Button>
        </div>
      ) : null}

      <label className="flex flex-col gap-1.5">
        <span className="flex items-baseline gap-2 text-[12.5px] font-semibold text-ink-800">
          Note for this visit
          <span className="text-[11.5px] font-normal text-ink-400">Carried into this visit’s job only</span>
        </span>
        <Textarea
          value={note}
          readOnly={!canSchedule}
          placeholder="e.g. Check the garage again. Client wants ants looked at in the yard."
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => {
            if (canSchedule && note.trim() !== (slot.note ?? '')) {
              saveNote.mutate({ ...target, note: note.trim() || null })
            }
          }}
          rows={3}
        />
      </label>

      {canSchedule ? (
        skipping ? (
          <div className="flex flex-wrap items-end gap-2 rounded-lg bg-ink-50 p-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-[12px] font-medium text-ink-700">
              Why is it skipped? (optional)
              <Input value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
            <Button size="sm" onClick={() => setSkipping(false)}>
              Keep it
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={skip.isPending}
              onClick={() => skip.mutate({ ...target, reason: reason.trim() || null })}
            >
              Skip this visit
            </Button>
          </div>
        ) : (
          <>
            {others.length > 0 ? (
              <fieldset className="flex flex-col gap-1.5">
                <legend className="mb-1.5 flex items-baseline gap-2 text-[12.5px] font-semibold text-ink-800">
                  Also on this job
                  <span className="text-[11.5px] font-normal text-ink-400">
                    {`Due ${companions.data ? (combineLabel(companions.data) ?? 'together') : 'together'} at this site`}
                  </span>
                </legend>
                {others.map((other) => {
                  const key = slotKey(other)
                  return (
                    <label key={key} className="flex items-center gap-2.5 text-[13px] text-ink-800">
                      <input
                        type="checkbox"
                        className="size-4 accent-ink-900"
                        checked={!unticked.has(key)}
                        disabled={create.isPending}
                        onChange={(event) => toggle(key, event.target.checked)}
                      />
                      <span className="min-w-0 flex-1 truncate font-medium">{andList(other.serviceNames)}</span>
                      <span className="text-[12px] text-ink-500 tabular-nums">{dayLabel(other.dueOn)}</span>
                    </label>
                  )
                })}
              </fieldset>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                icon={<Icon name="plus" className="size-4" />}
                loading={create.isPending && !opening}
                disabled={create.isPending}
                onClick={() => create.mutate(createInput)}
              >
                Create job now
              </Button>
              <Button
                loading={create.isPending && opening}
                disabled={create.isPending}
                onClick={() => {
                  setOpening(true)
                  create.mutate(
                    createInput,
                    {
                      onSuccess: (result) => overlay.openJob(result.jobId),
                      onSettled: () => setOpening(false),
                    },
                  )
                }}
              >
                Create and open
              </Button>
              <Button onClick={() => setSkipping(true)}>Skip this visit</Button>
            </div>
          </>
        )
      ) : null}

      {error ? <ErrorState message={errorMessage(error)} /> : null}
    </Panel>
  )
}

/** The rule, down the right; the agreement instead when it is part of one. Decision 96. */
export function ScheduleRail({
  job,
  data,
  canSchedule,
  canEditAgreement,
  onEdit,
}: {
  job: Job
  data: RepeatForJob
  canSchedule: boolean
  canEditAgreement: boolean
  onEdit: () => void
}) {
  if (!data.schedule) return null
  if (data.agreement) {
    return (
      <AgreementRail
        job={job}
        data={data}
        agreement={data.agreement}
        canSchedule={canSchedule}
        canEdit={canEditAgreement}
        onEdit={onEdit}
      />
    )
  }
  return <RuleRail data={data} schedule={data.schedule} canSchedule={canSchedule} onEdit={onEdit} />
}

function RuleRail({
  data,
  schedule,
  canSchedule,
  onEdit,
}: {
  data: RepeatForJob
  schedule: ScheduleView
  canSchedule: boolean
  onEdit: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const refresh = () => void queryClient.invalidateQueries(trpc.repeat.pathFilter())
  const stop = useMutation(trpc.repeat.stop.mutationOptions({ onSuccess: refresh }))
  const resume = useMutation(trpc.repeat.resume.mutationOptions({ onSuccess: refresh }))
  const [confirmStop, setConfirmStop] = useState(false)

  return (
    <div className="flex min-h-full flex-col gap-5">
      <div className="flex items-center gap-2">
        <Label>Repeat schedule</Label>
        {canSchedule ? (
          <button type="button" className="ml-auto text-[12.5px] font-semibold text-green-700 hover:underline" onClick={onEdit}>
            Edit
          </button>
        ) : null}
      </div>

      <dl className="flex flex-col divide-y divide-ink-100 text-[13px]">
        <Fact term="Repeats">{intervalLabel(schedule)}</Fact>
        <Fact term="Counts from">{anchorLabel(schedule.anchor)}</Fact>
        <Fact term="Services">
          <ServicesList services={schedule.services} />
        </Fact>
        <Fact term="Book with">
          {schedule.bookWith === 'UNASSIGNED'
            ? 'Nobody — left for the board'
            : schedule.bookWith === 'LAST_TECH'
              ? `Last tech (${schedule.bookWithName ?? 'nobody yet'})`
              : (schedule.bookWithName ?? '—')}
        </Fact>
        <Fact term="Ends">{endsLabel(schedule)}</Fact>
        <CreatedFact data={data} />
      </dl>

      <div className="flex flex-col gap-2">
        <Label>Carried onto every visit</Label>
        {schedule.standingNotes ? (
          <p className="rounded-lg bg-ink-50 px-3 py-2.5 text-[13px] whitespace-pre-wrap text-ink-800 ring-1 ring-ink-200 ring-inset">
            {schedule.standingNotes}
          </p>
        ) : (
          <p className="text-[13px] text-ink-400">Nothing yet.</p>
        )}
      </div>

      {canSchedule ? (
        <div className="mt-auto flex flex-col gap-2 pt-4">
          {schedule.stoppedAt ? (
            <>
              <p className="text-[12.5px] text-ink-500">
                Stopped on {new Date(schedule.stoppedAt).toLocaleDateString('en-AU')}.
              </p>
              <Button loading={resume.isPending} onClick={() => resume.mutate({ id: schedule.id })}>
                Resume schedule
              </Button>
            </>
          ) : confirmStop ? (
            <>
              <p className="text-[12.5px] text-ink-600">
                Nothing further is planned. Jobs already made stay as they are.
              </p>
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => setConfirmStop(false)}>
                  Keep it
                </Button>
                <Button
                  className="flex-1"
                  variant="danger"
                  loading={stop.isPending}
                  onClick={() => stop.mutate({ id: schedule.id }, { onSuccess: () => setConfirmStop(false) })}
                >
                  Stop schedule
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button
                className="w-full text-red-700 ring-red-200"
                icon={<Icon name="close" className="size-4" />}
                onClick={() => setConfirmStop(true)}
              >
                Stop schedule
              </Button>
              <p className="text-center text-[11.5px] text-ink-400">Jobs already created stay on the schedule.</p>
            </>
          )}
          {stop.error ?? resume.error ? (
            <ErrorState message={errorMessage(stop.error ?? resume.error)} />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The agreement this job's schedule is part of: who and how long, this site
 * and the others, and the two things done to a site from here.
 */
function AgreementRail({
  job,
  data,
  agreement,
  canSchedule,
  canEdit,
  onEdit,
}: {
  job: Job
  data: RepeatForJob
  agreement: SiteAgreement
  canSchedule: boolean
  canEdit: boolean
  onEdit: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const pause = useMutation(
    trpc.agreements.setSitePaused.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.repeat.pathFilter())
        void queryClient.invalidateQueries(trpc.agreements.pathFilter())
      },
    }),
  )
  const schedule = data.schedule!
  const thisSite = agreement.thisSite

  return (
    <div className="flex min-h-full flex-col gap-5">
      <div className="flex items-center gap-2">
        <Label>Agreement</Label>
        <Link
          to={`/agreements/${agreement.id}`}
          className="ml-auto text-[12.5px] font-semibold text-green-700 hover:underline"
        >
          {`Open AG-${agreement.number}`}
        </Link>
      </div>

      <dl className="flex flex-col divide-y divide-ink-100 text-[13px]">
        <Fact term="Client">{agreement.client.name}</Fact>
        <Fact term="Term">
          <span className="block">{`${dayLabel(agreement.startsOn)} – ${dayLabel(agreement.termEndsOn)}`}</span>
          <span className="block text-[11.5px] font-normal text-ink-500">
            {`${BILLING_LABEL[agreement.billing]}${agreement.autoRenew ? ' · renews' : ''}`}
          </span>
        </Fact>
        <Fact term="This site">
          {thisSite
            ? `${siteName(thisSite)} · ${thisSite.services} ${thisSite.services === 1 ? 'service' : 'services'}`
            : '—'}
        </Fact>
        <Fact term="Other sites">
          {agreement.otherSites.length ? agreement.otherSites.map(siteName).join(', ') : 'None'}
        </Fact>
        <CreatedFact data={data} />
      </dl>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Label>This service</Label>
          {canSchedule ? (
            <button type="button" className="ml-auto text-[12.5px] font-semibold text-green-700 hover:underline" onClick={onEdit}>
              Edit
            </button>
          ) : null}
        </div>
        <div className="text-[13px] text-ink-800">
          <ServicesList services={schedule.services} />
          <p className="mt-0.5 text-[12px] text-ink-500">{`${intervalLabel(schedule)} · ${anchorLabel(schedule.anchor).toLowerCase()}`}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Site access</Label>
        {job.site?.accessNotes ? (
          <p className="text-[13px] whitespace-pre-wrap text-ink-800">{job.site.accessNotes}</p>
        ) : (
          <p className="text-[13px] text-ink-400">Nothing recorded.</p>
        )}
      </div>

      {canEdit ? (
        <div className="mt-auto flex flex-col gap-2 pt-4">
          <Button className="w-full" icon={<Icon name="plus" className="size-4" />} onClick={() => setAdding(true)}>
            Add a service to this site
          </Button>
          <Button
            className="w-full text-red-700 ring-red-200"
            loading={pause.isPending}
            onClick={() =>
              pause.mutate({ agreementId: agreement.id, siteId: agreement.siteId, paused: !agreement.sitePaused })
            }
          >
            {agreement.sitePaused ? 'Resume this site' : 'Pause this site'}
          </Button>
          {pause.error ? <ErrorState message={errorMessage(pause.error)} /> : null}
        </div>
      ) : null}

      {adding ? (
        <AgreementBuilder
          open
          mode={{ kind: 'extend', agreementId: agreement.id, siteIds: [agreement.siteId] }}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  )
}

function ServicesList({ services }: { services: ScheduleView['services'] }) {
  if (!services.length) return <span className="text-ink-400">None</span>
  return (
    <ul className="flex flex-col gap-0.5">
      {services.map((service) => (
        <li key={service.serviceTypeId} className="flex justify-between gap-3">
          <span className="min-w-0 truncate">{service.name}</span>
          <span className="text-ink-600 tabular-nums">
            {service.priceIncTax === null ? '—' : money(service.priceIncTax)}
          </span>
        </li>
      ))}
      {services.length > 1 ? (
        <li className="flex justify-between gap-3 font-semibold">
          <span>Each visit</span>
          <span className="tabular-nums">{money(servicesTotal(services))}</span>
        </li>
      ) : null}
    </ul>
  )
}

function CreatedFact({ data }: { data: RepeatForJob }) {
  return (
    <Fact term="Created">
      <span className="block">{`${leadLabel(data.settings.leadDays)} before due`}</span>
      <span className="block text-[11.5px] text-ink-400">
        {`${data.settings.autoCreate ? 'Automatically' : 'By hand'}${data.settings.autoBook ? ', booked in' : ''} · Settings → Scheduling`}
      </span>
    </Fact>
  )
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_1fr] gap-3 py-2.5 first:pt-0">
      <dt className="text-ink-500">{term}</dt>
      <dd className="min-w-0 font-medium text-ink-900">{children}</dd>
    </div>
  )
}

/**
 * A job from an imported ServiceM8 series, with no schedule yet: the series'
 * jobs as they happened, and Start schedule prefilled from its interval.
 */
function ImportedChain({
  data,
  canSchedule,
  onStart,
}: {
  data: RepeatForJob
  canSchedule: boolean
  onStart: (legacyRecurrenceId: string | null) => void
}) {
  const hrefFor = useJobOverlay().hrefFor
  const imported = data.imported!
  const earlier = imported.total - imported.jobs.length

  return (
    <Panel className="gap-4">
      <PanelHeading
        title="From ServiceM8"
        hint={imported.interval ? intervalLabel(imported.interval) : 'Repeating series'}
      />
      <p className="text-[13px] text-ink-600">
        This job is one of a repeating series brought over from ServiceM8. The series is read here
        as it was; nothing makes its next job until it has a schedule.
      </p>
      <ol aria-label="Series jobs" className="flex flex-col divide-y divide-ink-100 rounded-lg ring-1 ring-ink-200 ring-inset">
        {earlier > 0 ? (
          <li className="px-3 py-2 text-[12px] text-ink-500">{earlier} earlier</li>
        ) : null}
        {imported.jobs.map((row) => (
          <li
            key={row.id}
            aria-current={row.current ? 'step' : undefined}
            className={cn('flex items-center gap-3 px-3 py-2 text-[13px]', row.current && 'bg-blue-50')}
          >
            <span className="w-28 shrink-0 tabular-nums text-ink-700">{dayLabel(row.on)}</span>
            {row.current ? (
              <span className="font-mono text-ink-500">{formatJobNumber(row)}</span>
            ) : (
              <Link to={hrefFor(row.id)} className="font-mono text-brand-700 hover:underline">
                {formatJobNumber(row)}
              </Link>
            )}
            {row.current ? (
              <span className="ml-auto text-[11px] font-bold tracking-wide text-blue-800 uppercase">
                This job
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      {canSchedule ? (
        <Button
          variant="primary"
          className="self-start"
          icon={<Icon name="repeat" className="size-4" />}
          onClick={() => onStart(imported.recurrenceId)}
        >
          Start schedule
        </Button>
      ) : null}
    </Panel>
  )
}
